import { Typography } from '@material-ui/core'
import { withStyles, WithStyles, WithTheme } from '@material-ui/core/styles'
import createStyles from '@material-ui/core/styles/createStyles'
import flatten from 'lodash/flatten'
import React, { useState } from 'react'
import { AutoSizer } from 'react-virtualized'
import { Hint, Sankey } from 'react-vis'

import { ILink, INode } from '../types'
import { formatNumber } from '../utils'

import { useGraphContext } from './GraphWrapper/GraphWrappers'

const DEFAULT_OPACITY = 0.3
const HOVERED_OPACITY = 0.6

const styles = () =>
  createStyles({
    tooltip: {
      backgroundColor: 'white',
      padding: 8,
      borderRadius: 4,
    },
  })

interface IProps extends WithTheme, WithStyles {
  links: ILink[]
  nodes: INode[]
}

interface IHint {
  linkIndex?: number
  value: Record<string, unknown>
  x?: number
  y: number
}

interface ILinkData {
  index: number
  source: INode
  target: INode
  value: number
  y0: number
  y1: number
}

interface INodeData {
  index: number
  key: string
  value: number
  x0: number
  y: number
}

const SankeyPlot = (props: IProps) => {
  const { classes, theme } = props
  const { fontFamily } = theme.typography
  const [activeNode, setActiveNode] = useState<INode | null>(null)
  const [nodes, setNodes] = useState<INode[]>(props.nodes)
  const [links, setLinks] = useState<ILink[]>(props.links)
  const [hint, setHint] = useState<IHint | null>(null)

  const { reactVis } = useGraphContext()

  const getNodeName = (index: number) => (nodes[index] ? nodes[index].name : '')

  const resetHint = () => setHint(null)

  const onLinkMouseOver = (linkData: ILinkData) => {
    const { index: linkIndex, source, target, value, y0, y1 } = linkData
    setHint({
      linkIndex,
      y: y0 + (y1 - y0) / 2,
      value: { [`${source.name} ➞ ${target.name}`]: formatNumber(value) },
    })
  }

  const onValueMouseOver = (nodeData: INodeData) => {
    const { index: nodeIndex, key, value: total, x0, y } = nodeData
    const isOrigin = key.startsWith('origin')
    const value = links
      .filter((l) => (isOrigin ? l.source === nodeIndex : l.target === nodeIndex))
      .map((l) => ({
        [`${getNodeName(l.source)} -> ${getNodeName(l.target)}`]: formatNumber(l.value),
      }))
      .reduce((acc, prev) => ({ ...acc, ...prev }), {
        [`Total trips`]: formatNumber(total),
      })

    setHint({
      x: isOrigin ? x0 + 40 : x0 - 40, // To avoid having the tooltip on the node
      y,
      value,
    })
  }

  const onValueClick = (node: any) => {
    if (activeNode) {
      // If we have already clicked on a node, we go back to normal
      setActiveNode(null)
      setNodes(props.nodes)
      setLinks(props.links)
    } else {
      setActiveNode(node)
      const linksWithActiveNode = props.links.filter((link) => link.source === node.index || link.target === node.index)
      const nodeIndexes = flatten(linksWithActiveNode.map((l) => [l.source, l.target]))

      const newNodes: INode[] = []
      const mapOldToNewIndex: { [index: number]: number } = {}
      props.nodes.forEach((n, i: number) => {
        if (nodeIndexes.includes(i)) {
          newNodes.push(n)
          mapOldToNewIndex[i] = newNodes.length - 1 // index of n
        }
      })
      const newLinks = linksWithActiveNode.map((l) => ({
        ...l,
        source: mapOldToNewIndex[l.source],
        target: mapOldToNewIndex[l.target],
      }))

      setLinks(newLinks)
      setNodes(newNodes)
    }
  }

  const linksWithOpacity = links.map((d, i) => ({
    ...d,
    opacity: hint && i === hint.linkIndex ? HOVERED_OPACITY : DEFAULT_OPACITY,
  }))

  return (
    <div style={{ flex: 1, textAlign: 'left', height: 700 }}>
      <AutoSizer disableHeight={true}>
        {({ width }) => (
          <Sankey
            ref={reactVis.sankeyRef}
            // Disable animation because it messes up the onValueClick
            // https://github.com/uber/react-vis/issues/381
            animation={false}
            nodes={nodes}
            links={linksWithOpacity}
            height={700}
            width={width}
            nodeWidth={16}
            // do not use voronoi in combination with mouse events
            hasVoronoi={false}
            onLinkMouseOver={onLinkMouseOver}
            onLinkMouseOut={resetHint}
            onValueClick={onValueClick}
            onValueMouseOver={onValueMouseOver}
            onValueMouseOut={resetHint}
            nodePadding={16}
            margin={24}
            // The nodes will always be kept in the same order
            layout={0}
            style={{ labels: { fontFamily } }}
          >
            {hint && (
              <Hint x={hint.x || width / 2} y={hint.y} value={hint.value}>
                <div className={classes.tooltip}>
                  {Object.entries(hint.value).map(([key, val]) => (
                    <Typography key={key} variant="subtitle1">
                      {`${key}: ${val}`}
                    </Typography>
                  ))}
                </div>
              </Hint>
            )}
          </Sankey>
        )}
      </AutoSizer>
    </div>
  )
}

export default withStyles(styles, { withTheme: true })(SankeyPlot)
